import styled from 'styled-components';

import { colors, constants, media } from '../../../core';
import Icon from '../Icon';
import Tag from '../Tag';

export const TableFrame = styled.div`
  flex: 1;
  position: relative;
  display: flex;
  flex-direction: column;
`;

export const TableWrapper = styled.table`
  border-collapse: collapse;
  border-spacing: 0;
  width: 100%;
  font-family: ${({ theme }) => theme.fontFamily || 'Quicksand, sans-serif'};
`;

export const TableHead = styled.thead`
  border-bottom: 1px solid ${({ theme }) => theme.tableBorderColor || colors.cloud};

  h6 {
    font-weight: 600;
  }

  ${media.tabletLandscape} {
    display: none;
  }
`;

export const TableHeadRow = styled.tr``;

export const TableBody = styled.tbody``;

export const TableBodyRow = styled.tr<{
  selected: boolean;
  expanded?: boolean;
  devideIndex?: number;
  gridTemplate?: any;
}>`
  cursor: pointer;

  ${media.tabletLandscape} {
    vertical-align: top;

    svg {
      margin-top: 4px;
    }

    input[type='checkbox'] {
      margin-top: 12px;
    }
  }

  #cell_mask svg {
    visibility: hidden;
    cursor: pointer;

    > path {
      fill: ${({ theme }) => theme.tableIconColor || colors.primary};
    }

    ${() =>
      constants.IS_TOUCH_DEVICE &&
      `
        visibility: visible;
      `}

    ${media.tabletLandscape} {
      visibility: visible;
    }
  }

  &:nth-child(odd) {
    background-color: ${({ expanded, theme }) =>
      expanded
        ? theme.tableRowExpandedPrimaryBackgroundColor || colors.palegreen
        : theme.tableRowPrimaryBackgroundColor || colors.white};
  }

  &:nth-child(even) {
    background-color: ${({ expanded, theme }) =>
      expanded
        ? theme.tableRowExpandedSecondaryBackgroundColor || colors.primaryOpacity
        : theme.tableRowSecondaryBackgroundColor || colors.pearl};
  }

  &:hover {
    background-color: ${({ expanded, theme }) =>
      expanded ? 'none' : theme.tableRowBackgroundColorHover || colors.smoke};

    #cell_mask svg {
      visibility: visible;
    }
  }

  ${({ selected, theme }) =>
    selected &&
    `
    && {
      background-color: ${theme.tableRowBackgroundColorSelected || colors.primaryOpacity};
    }
  `}

  ${({ expanded, devideIndex }) =>
    expanded &&
    `      
      &.row td:nth-child(${devideIndex}) {
        border-right: 1px solid ${colors.smoke};
      }

      &.row td:nth-child(-n+${devideIndex}) {
        vertical-align: top;
      }
    `}

  ${media.tabletLandscape} {
    ${({ gridTemplate, theme }) =>
      gridTemplate &&
      `
      display: grid;
      grid-template: ${gridTemplate};
      grid-auto-columns: auto;
      padding: ${theme.spacing.small || '16px'};
      gap: 8px;
  `}
  }
`;

export const TableHeader = styled.th`
  color: ${({ theme }) => theme.tableHeaderTextColor || colors.charcoal};
  padding: 16px 20px;
  text-align: left;
`;

export const TableCell = styled.td<{
  fixedWidth?: number;
  noPadding?: boolean;
  selected?: boolean;
  gridArea?: string;
}>`
  color: ${({ theme }) => theme.tableCellTextColor || colors.ash};
  font-size: 14px;

  padding: ${({ noPadding }) => (noPadding ? '0 20px' : '20px')};
  text-align: left;

  ${({ fixedWidth }) =>
    fixedWidth &&
    `
    width: ${fixedWidth}px;
  `}

  ${Tag} {
    margin-top: 5px;
  }

  ${({ selected, theme }) =>
    selected &&
    `
    && {
      color: ${theme.tableCellSelectedTextColor || colors.darkgray};
    }
  `}

  ${media.tabletLandscape} {
    ${({ gridArea }) =>
      gridArea &&
      `
      padding: 0;
      grid-area: ${gridArea};
    `}
  }
`;

export const PaginationWrapper = styled.div`
  margin: 0 auto;
  max-width: 600px;
  padding: 0;

  .pagination {
    padding: 20px;
    display: flex;
    justify-content: space-between;
    list-style: none;
    cursor: pointer;
  }

  .page,
  .break {
    color: ${({ theme }) => theme.tablePaginationTextColor || colors.primary};
  }

  a:focus {
    outline: 0;
  }

  .link {
    padding: 3px 10px;
    border-radius: 3px;
  }

  .active a {
    color: ${({ theme }) => theme.tablePaginationTextColorActive || colors.ash};
    background: ${({ theme }) =>
      theme.tablePaginationBackgroundColorActive || colors.primaryOpacity};
    border: none;
    outline: 0;
  }
`;

export const RowContainer = styled.div`
  align-items: center;
  display: flex;
`;

export const IconWrapper = styled.div<{ removeFilters: boolean }>`
  visibility: ${({ removeFilters }) => (removeFilters ? 'hidden' : 'visible')};
  margin-left: 6px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

export const StyledIcon = styled(Icon)`
  cursor: pointer;
`;

export const CheckBox = styled.input`
  -webkit-appearance: none;
  appearance: none;
  background-color: #fff;
  margin: 0;
  font: inherit;
  width: 1.15em;
  height: 1.15em;
  border-radius: 3px;
  background-color: ${({ theme }) => theme.checkboxBackgroundColor || colors.white};
  border: 1px solid ${({ theme }) => theme.checkboxBorderColor || colors.smoke};
  transform: translateY(-0.075em);
  display: grid;
  place-content: center;

  &:before {
    content: '';
    width: 0.65em;
    height: 0.65em;
    transform: scale(0);
    transition: 120ms transform ease-in-out;
    transform-origin: bottom left;
    clip-path: polygon(14% 44%, 0 65%, 50% 100%, 100% 16%, 80% 0%, 43% 62%);
    box-shadow: inset 1em 1em #fafafa;
  }

  &:checked {
    background-color: ${({ theme }) => theme.checkboxBackgroundActiveColor || colors.primary};
    &:before {
      transform: scale(1);
    }
  }

  &:focus,
  &:hover {
    border: 1px solid ${({ theme }) => theme.checkboxHover || colors.primaryHover};
    box-shadow: 0 0 3px ${({ theme }) => theme.checkboxShadow || colors.primaryHover};
  }
`;

export const MaskIconWrapper = styled.div<{ class: string }>``;

export const HeaderRow = styled.div`
  display: flex;
  flex-direction: row-reverse;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.tableBorderColor || colors.cloud};
`;

export const CustomHeading = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
